import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon } from 'lucide-react';
const situations = [{
  title: 'The "Silent Dinner" Syndrome:',
  text: 'They eat at lightning speed, eyes glazed over, barely speaking a word, just so they can sprint back to their screen the second they swallow their last bite.',
  keywords: ['eyes glazed over', 'sprint back to their screen']
}, {
  title: 'The Ghost in the House:',
  text: "You ask for a simple favor—to take out the trash or set the table—and you get... nothing. Absolute silence. They've learned to tune out your voice like background noise.",
  keywords: ['nothing. Absolute silence', 'tune out your voice']
}, {
  title: 'The Endless Procrastination:',
  text: 'Homework, chores, and basic responsibilities are delayed for hours. They promise "just 5 more minutes" until it\'s 10 PM and the screaming match begins.',
  keywords: ['"just 5 more minutes"', 'screaming match begins']
}, {
  title: 'The "Polite" Failure:',
  text: "You tried asking nicely. You tried explaining why it's bad. You tried being their friend. But they look right through you as if you don't exist.",
  keywords: ['look right through you', "you don't exist"]
}, {
  title: 'The Aggression Spike:',
  text: 'When the Wi-Fi goes off, the monster comes out. Crying, screaming, and even disrespectful insults that make you wonder: "Who is this child and where did my sweet baby go?"',
  keywords: ['the monster comes out', 'where did my sweet baby go']
}, {
  title: 'Loss of Authority:',
  text: "You feel like a hostage in your own home. You are afraid to set limits because you dread the emotional explosion that follows. You've lost control, and they know it.",
  keywords: ['hostage in your own home', 'lost control, and they know it']
}];
function highlightKeywords(text: string, keywords: string[]) {
  let result = text;
  keywords.forEach(keyword => {
    result = result.replace(keyword, `<strong class="text-[#1a1a2e] font-bold">${keyword}</strong>`);
  });
  return result;
}
export function ChecklistSection() {
  return <section className="w-full bg-white py-10 md:py-16 px-6" data-id="element-15">
      <div className="max-w-3xl mx-auto" data-id="element-16">
        {/* Header */}
        <div className="text-center mb-6" data-id="element-17">
          <span className="inline-block text-4xl mb-4" data-id="element-18">🔴</span>
          <h2 className="font-black text-3xl md:text-4xl text-[#1a1a2e] leading-tight" data-id="element-19">
            DOES THIS SOUND <span className="text-red-600" data-id="element-20">TERRIFYINGLY</span>{' '}
            FAMILIAR?
          </h2>
        </div>

        <p className="text-center text-lg md:text-xl text-gray-700 mb-12 leading-relaxed" data-id="element-21">
          If you check more than 2 of these boxes, your child isn't just
          "playing"—they are{' '}
          <span className="font-bold text-[#1a1a2e]" data-id="element-22">
            drifting away from you
          </span>
          .
        </p>

        {/* Checklist Items */}
        <div className="space-y-5" data-id="element-23">
          {situations.map((item, index) => <motion.div key={index} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          duration: 0.4
        }} className="flex items-start gap-4 p-5 md:p-6 bg-red-50 border border-red-100 rounded-xl" data-id="element-24">
              <span className="flex-shrink-0 text-xl mt-0.5" data-id="element-25">❌</span>
              <div data-id="element-26">
                <p className="font-bold text-lg md:text-xl text-[#1a1a2e] mb-1" data-id="element-27">
                  {item.title}
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
              __html: highlightKeywords(item.text, item.keywords)
            }} data-id="element-28" />
              </div>
            </motion.div>)}
        </div>

        {/* Brain Damage Image */}
        <div className="mt-10 rounded-2xl overflow-hidden shadow-xl" data-id="element-29">
          <img src="https://cdn.magicpatterns.com/uploads/emik7Kcm66b3BnSo9gvNFP/abdc4af0-c22b-4c06-b384-d26cf31290ca.png" alt="Child's brain being affected by screen addiction" className="w-full h-auto object-cover" data-id="element-30" />
        </div>

        {/* Brutal Truth Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mt-14 p-8 md:p-10 bg-[#1a1a2e] rounded-2xl text-white" data-id="element-31">
          <div className="flex items-center gap-3 mb-6" data-id="element-32">
            <AlertTriangleIcon className="w-8 h-8 text-yellow-400 flex-shrink-0" data-id="element-33" />
            <h3 className="font-black text-2xl md:text-3xl" data-id="element-34">
              THE BRUTAL TRUTH
            </h3>
          </div>

          <p className="text-lg md:text-xl leading-relaxed mb-6 text-gray-200" data-id="element-35">
            Every time you yell to get them off the Xbox or Tablet, you aren't
            just losing the argument—you are{' '}
            <span className="font-bold text-white" data-id="element-36">losing the bond</span>.
          </p>

          <p className="text-lg md:text-xl leading-relaxed text-gray-200" data-id="element-37">
            Each scream adds another{' '}
            <span className="font-bold text-red-400" data-id="element-38">brick to the wall</span>{' '}
            between you and your child. If you don't change the protocol{' '}
            <span className="font-bold text-yellow-400" data-id="element-39">tonight</span>, that
            wall will eventually become{' '}
            <span className="font-bold text-white" data-id="element-40">
              too high for you to ever climb over again
            </span>
            .
          </p>
        </motion.div>
      </div>
    </section>;
}